import React, { useEffect, useState } from 'react';
import { usePrefersReducedMotion } from '../usePrefersReducedMotion';

// Illustrative only: invented businesses, clearly labelled as such below.
const SCENARIOS = [
  {
    question: '¿Cuál es la mejor clínica dental en Getafe?',
    intro: 'Estas son algunas de las clínicas dentales mejor valoradas en Getafe:',
    results: ['Clínica Dental Alameda', 'Sonrisa Centro Getafe', 'Dental Plaza Sur'],
    verdict: 'Tu clínica no aparece'
  },
  {
    question: '¿Dónde compro pan de masa madre en Malasaña?',
    intro: 'Si buscas pan de masa madre en Malasaña, te recomiendo:',
    results: ['Horno La Espiga', 'Masa Viva Obrador', 'Panadería del Barrio'],
    verdict: 'Tu panadería no aparece'
  },
  {
    question: 'Necesito un fontanero urgente en Móstoles',
    intro: 'Estos fontaneros de Móstoles ofrecen servicio de urgencia:',
    results: ['Fontanería Rápida Sur', 'Aguas Móstoles 24h', 'Reparaciones Tomé'],
    verdict: 'Tu empresa no aparece'
  }
];

const TYPE_MS = 34;
const LINE_MS = 420;
const HOLD_MS = 3200;

const pad = (n) => String(n).padStart(2, '0');

const HeroDemo = () => {
  const reduceMotion = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);
  const [typed, setTyped] = useState(0);
  const [shownLines, setShownLines] = useState(0);
  const [phase, setPhase] = useState('typing'); // typing | thinking | answering | verdict

  const scenario = SCENARIOS[index];

  useEffect(() => {
    const current = SCENARIOS[index];

    if (reduceMotion) {
      setTyped(current.question.length);
      setShownLines(current.results.length);
      setPhase('verdict');
      return undefined;
    }

    const timers = [];
    const at = (ms, fn) => timers.push(setTimeout(fn, ms));

    setTyped(0);
    setShownLines(0);
    setPhase('typing');

    const questionLength = current.question.length;
    for (let i = 1; i <= questionLength; i += 1) {
      at(i * TYPE_MS, () => setTyped(i));
    }

    let t = questionLength * TYPE_MS + 250;
    at(t, () => setPhase('thinking'));
    t += 950;
    at(t, () => setPhase('answering'));
    current.results.forEach((_, i) => at(t + 150 + i * LINE_MS, () => setShownLines(i + 1)));
    t += 150 + current.results.length * LINE_MS + 300;
    at(t, () => setPhase('verdict'));
    at(t + HOLD_MS, () => setIndex((prev) => (prev + 1) % SCENARIOS.length));

    return () => timers.forEach(clearTimeout);
  }, [index, reduceMotion]);

  const showAnswer = phase === 'answering' || phase === 'verdict';

  return (
    <div>
      <p className="sr-only">
        Ejemplo ilustrativo: alguien le pregunta a una IA cuál es la mejor clínica dental en Getafe,
        la IA recomienda tres clínicas y la clínica del usuario no aparece.
      </p>

      <figure
        aria-hidden="true"
        className="relative rounded-[26px] bg-[var(--lp-paper)] p-6 text-[var(--lp-ink)] shadow-[14px_14px_0_var(--lp-lime)] sm:p-8"
      >
        <div className="flex items-center justify-between lp-mono text-[11px] uppercase tracking-[0.14em] text-black/45">
          <span className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[var(--lp-alarm)]" />
            Consulta · IA con búsqueda web
          </span>
          <span>{pad(index + 1)}/{pad(SCENARIOS.length)}</span>
        </div>

        <div className="mt-6 flex justify-end">
          <p className="max-w-[90%] rounded-[20px] rounded-br-md bg-[var(--lp-ink)] px-4 py-3 text-[15px] leading-snug text-[var(--lp-paper)] sm:text-base">
            {scenario.question.slice(0, typed)}
            {phase === 'typing' && <span className="lp-caret" />}
          </p>
        </div>

        <div className="mt-6 min-h-[196px]">
          {phase === 'thinking' && (
            <div className="flex items-center gap-3">
              <span className="lp-mono text-sm text-black/45">Buscando en la web…</span>
              <span className="lp-scan h-[3px] w-24 rounded-full bg-black/10" />
            </div>
          )}

          {showAnswer && (
            <>
              <p className="lp-rise text-[15px] leading-relaxed text-black/70">{scenario.intro}</p>
              <ol className="mt-4 space-y-2.5">
                {scenario.results.slice(0, shownLines).map((name, i) => (
                  <li key={name} className="lp-rise flex items-baseline gap-3 border-b border-black/10 pb-2.5">
                    <span className="lp-mono text-xs text-black/35">{pad(i + 1)}</span>
                    <span className="text-[17px] font-semibold tracking-[-0.01em]">{name}</span>
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>

        {phase === 'verdict' && (
          <div
            key={index}
            className="lp-stamp lp-display absolute -bottom-6 right-5 rounded-lg border-[3px] border-[var(--lp-alarm)] bg-[var(--lp-paper)] px-4 py-2 text-xl uppercase text-[var(--lp-alarm)] sm:right-8 sm:text-2xl"
          >
            {scenario.verdict}
          </div>
        )}
      </figure>

      <p className="mt-16 lp-mono text-[11px] uppercase tracking-[0.14em] text-[var(--lp-muted)]">
        Ejemplo ilustrativo · negocios ficticios
      </p>
    </div>
  );
};

export default HeroDemo;
